import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RouteParamsWithLanguage, StopInfoProps } from "../../../../../types/screensTypes";
import Button from "../../../../../components/ui/Button";
import { saveStop, getStopById } from "../../../../../api";
import { FileInput } from "../../../../../components/common/FileInput";
import TitleSubtitle from "../../../../../components/common/TitleSubtitle";
import TextInput from "../../../../../components/ui/TextInput";
import { useTrackCreation } from "../../../../../context/TrackCreationContext";
import { uploadFileToS3Complete } from "../../../../../api/s3";
import ImageList from "../../../../../components/images/ImageList";
import ImageSpecsBadges from "../../../../../components/common/ImageSpecsBadges";
import { useImageValidation } from "../../../../../hooks/useImageValidation";
import { IMAGE_FORMAT, IMAGE_MAX_SIZE_200_KB } from "../../../../../utils/image";
import stopEdit from "./StopEdit.module.css";
import stepStyles from "../../createStep.module.css";

const StopInfo: React.FC<StopInfoProps> = ({ navigateToNext }) => {
    const { trackData } = useTrackCreation();
    const [title, setTitle] = useState<string>("");
    const [schedule, setSchedule] = useState<string>("");
    const [path, setPath] = useState<string>("");
    const { wptId, language } = useParams<RouteParamsWithLanguage>();
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const imageValidation = useImageValidation({
        type: "fullscreen",
        imageUrl: path || undefined,
    });

    const saveStopAPI = async (): Promise<void> => {
        const token = localStorage.getItem("token"); // Retrieve token (or other logic to get it)
        const trackId = trackData.trackId; // Get the track ID from the context

        if (!wptId || !trackId || !token || !language) {
            return;
        }

        await saveStop({
            token,
            trackId: trackId,
            waypointId: wptId,
            language,
            name: title,
            schedule,
            pictureUrl: path,
        });

        navigateToNext(wptId, language);
    };

    const getWaypoint = async (id: string): Promise<void> => {
        const token = localStorage.getItem("token");
        if (!token || !language) {
            return;
        }
        const waypointData = await getStopById({ token, id, language });

        if (waypointData) {
            setTitle(waypointData.waypoint?.waypointHohoStopLocalized[0].name);
            setSchedule(waypointData.waypoint?.waypointHohoStopLocalized[0].schedules || "");
            setPath(waypointData.waypoint?.pictureUrl || "");
        }
    };

    useEffect(() => {
        if (wptId && wptId !== "") {
            getWaypoint(wptId);
        }
    }, [wptId, language]);

    const handleFileUpload = async (file: File | File[]) => {
        if (Array.isArray(file)) {
            console.error("Multiple files not supported");
            return;
        }
        const token = localStorage.getItem("token");
        if (!token) {
            console.error("No authentication token found");
            return;
        }

        await imageValidation.validateImage(file);

        uploadFileToS3Complete(
            {
                file,
                isPublic: true, // Public files for general uploads
                isFolder: false, // No folder structure for general uploads
            },
            token,
        )
            .then((result: string) => {
                setPath(result);
            })
            .catch((error) => console.error("Error uploading image:", error));
    };

    const setPicturesFromList = (pictures: string[]): void => {
        if (pictures && pictures.length > 0) {
            setPath(pictures[0]);
        } else {
            setPath("");
        }
    };

    return (
        <div className={`experience-stop-edit ${stopEdit.root}`}>
            <TitleSubtitle title={t("fillTheInfosOfYourStop")} subtitle={t("stopInfoSubtitle")} layout="step" />
            <div className={`experience-stop-field ${stopEdit.field}`}>
                <h4 className={`experience-stop-field-label ${stopEdit.fieldLabel}`}>{t("stopName")}</h4>
                <TextInput
                    placeholder={t("name")}
                    value={title}
                    size="small"
                    onChange={(e) => setTitle(e.target.value)}
                />
            </div>

            <div className={`experience-stop-field ${stopEdit.field}`}>
                <h4 className={`experience-stop-field-label ${stopEdit.fieldLabel}`}>{t("stopSchedule")}</h4>
                <TextInput
                    placeholder={t("schedule")}
                    value={schedule}
                    size="small"
                    onChange={(e) => setSchedule(e.target.value)}
                />
            </div>

            <div className={stepStyles.fileUpload}>
                <h4 className={`experience-stop-field-label ${stopEdit.fieldLabel}`}>{t("uploadPicture")}</h4>
                <ImageSpecsBadges spec="fullscreen" warnings={imageValidation.warnings} />
                <FileInput
                    onFileSelected={handleFileUpload}
                    showButton={true}
                    buttonText={t("uploadPicture")}
                    acceptedFileTypes={IMAGE_FORMAT}
                    maxSize={IMAGE_MAX_SIZE_200_KB}
                    maxSizeLabel={t("imageMaxSize200kb")}
                />
            </div>

            <div className="photo-gallery">
                <ImageList pictures={path ? [path] : []} setPictures={(pics: string[]) => setPicturesFromList(pics)} />
            </div>

            <div className={`${stepStyles.stepButton} ${stepStyles.footer}`}>
                <Button
                    variant="primary"
                    shape="pill"
                    className={stepStyles.continueBtn}
                    label={t("continue")}
                    onClick={saveStopAPI}
                />
            </div>
        </div>
    );
};

export default StopInfo;
